import { Card, Col, DatePicker, Form, Input, Row, Select } from 'antd'
import { useState } from 'react'

import { useTreatmentServiceGetHivAnamnesisDoctorsEndpoint } from 'src/api/queries'
import { requiredRules } from 'src/app-constants'
import { InternalInstitutionsSelect } from 'src/components'
import { useDebounce } from 'src/hooks'
import {
  dayMonthYearHourMinutesFormatWithDot,
  disabledFutureDate
} from 'src/libs'
import { useLocales } from 'src/locales'

const SELECTED = '__selected'

type TextSelectProps = {
  value?: string
  onChange?: (value: string) => void
  placeholder?: string
}

// The form stores the institution name as text; the select only picks it from the system
const RegistrationInstitutionSelect = ({
  value,
  onChange,
  placeholder
}: TextSelectProps) => (
  <InternalInstitutionsSelect
    placeholder={placeholder}
    value={value ? SELECTED : undefined}
    initialOptions={value ? [{ value: SELECTED, label: value }] : []}
    onItemSelect={(item) => onChange?.(item?.name ?? '')}
  />
)

// Users of the current institution; picking one also fills their phone
const RegistrationPersonSelect = ({
  value,
  onChange,
  placeholder,
  onPhone
}: TextSelectProps & { onPhone: (phone?: string | null) => void }) => {
  const [search, setSearch] = useState<string>()
  const debouncedSearch = useDebounce(search)

  const { data, isFetching } =
    useTreatmentServiceGetHivAnamnesisDoctorsEndpoint(
      { searchTerm: debouncedSearch, ids: [] },
      [debouncedSearch],
      { staleTime: 1000 * 60 }
    )

  const users = data?.data ?? []
  const options = users.map((user) => ({
    value: user.fullName ?? '',
    label: user.fullName
  }))

  return (
    <Select
      allowClear
      showSearch
      filterOption={false}
      onSearch={setSearch}
      loading={isFetching}
      placeholder={placeholder}
      value={value || undefined}
      options={
        value && !options.some((option) => option.value === value)
          ? [{ value, label: value }, ...options]
          : options
      }
      onChange={(name?: string) => {
        onChange?.(name ?? '')
        onPhone(users.find((user) => user.fullName === name)?.phoneNumber)
      }}
    />
  )
}

export const EpidemiologyRegistrationCard = () => {
  const { t } = useLocales()
  const form = Form.useFormInstance()

  const onPhone = (phone?: string | null) =>
    form.setFieldValue(['form', 'registration', 'personPhone'], phone ?? '')

  return (
    <Card title={t('epidemiology.registration_title')}>
      <Row gutter={[24, 24]}>
        <Col span={8}>
          <Form.Item
            name={['form', 'registration', 'institutionTitle']}
            label={t('epidemiology.institution_that_registered')}
            rules={requiredRules}
          >
            <RegistrationInstitutionSelect
              placeholder={t('epidemiology.institution_that_registered')}
            />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            name={['form', 'registration', 'registrationDate']}
            label={t('epidemiology.date_time_registration')}
            rules={requiredRules}
          >
            <DatePicker
              className="full-width"
              showTime
              showSecond={false}
              needConfirm={false}
              format={dayMonthYearHourMinutesFormatWithDot}
              disabledDate={disabledFutureDate}
              placeholder={t('epidemiology.date_time_registration')}
            />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            name={['form', 'registration', 'updateDate']}
            label={t('epidemiology.updated_date')}
            rules={requiredRules}
          >
            <DatePicker
              className="full-width"
              showTime
              showSecond={false}
              needConfirm={false}
              format={dayMonthYearHourMinutesFormatWithDot}
              disabledDate={disabledFutureDate}
              placeholder={t('epidemiology.updated_date')}
            />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            name={['form', 'registration', 'personFullName']}
            label={t('epidemiology.person_who_registered')}
            rules={requiredRules}
          >
            <RegistrationPersonSelect
              placeholder={t('epidemiology.person_who_registered')}
              onPhone={onPhone}
            />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            name={['form', 'registration', 'personPhone']}
            label={t('common.phone')}
          >
            <Input placeholder={t('common.phone')} />
          </Form.Item>
        </Col>
      </Row>
    </Card>
  )
}
